import React, { useState, useEffect, useCallback } from 'react';
import { X, Clock, Route, Layers, Server, ChevronDown, ChevronUp } from 'lucide-react';

const API_BASE = '/internal/analytics';

const token = () => localStorage.getItem('auth_token') || localStorage.getItem('saige_admin_token');

const apiFetch = async (path) => {
    const res = await fetch(`${API_BASE}${path}`, {
        headers: { Authorization: `Bearer ${token()}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
};

const relativeTime = (iso) => {
    if (!iso) return '—';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

const EVENT_CATEGORY_COLORS = {
    engagement: '#818cf8',
    acquisition: '#34d399',
    activation: '#a78bfa',
    outcome: '#60a5fa',
    revenue: '#fb923c',
    system: '#f87171',
    admin: '#f59e0b',
    uncategorized: '#64748b',
};

// ─────────────────────────────────────────────────────────────────────────────
// EVENT ROW
// ─────────────────────────────────────────────────────────────────────────────

const EventRow = ({ event, index }) => {
    const [expanded, setExpanded] = useState(false);
    const catColor = EVENT_CATEGORY_COLORS[event.event_category] || '#64748b';
    const preview = event.event_data_preview || {};
    const hasPreview = Object.keys(preview).length > 0;

    return (
        <div style={{
            borderBottom: '1px solid #1e293b',
            background: index % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
        }}>
            <div
                style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 16px', cursor: hasPreview ? 'pointer' : 'default',
                }}
                onClick={() => hasPreview && setExpanded(e => !e)}
            >
                {/* Timestamp */}
                <div style={{ fontSize: 10, color: '#64748b', width: 60, flexShrink: 0, fontFamily: 'monospace' }}>
                    {relativeTime(event.created_at)}
                </div>

                {/* Category dot */}
                <div style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: catColor, flexShrink: 0,
                }} />

                {/* Event name */}
                <div style={{
                    fontSize: 12, fontFamily: 'monospace', color: '#e2e8f0',
                    flex: 1, minWidth: 0, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {event.event_name}
                </div>

                {/* Route */}
                {event.route && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: 10, color: '#475569', flexShrink: 0,
                    }}>
                        <Route size={9} />
                        {event.route}
                    </div>
                )}

                {/* Feature area */}
                {event.feature_area && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: 10, color: '#334155', flexShrink: 0,
                    }}>
                        <Layers size={9} />
                        {event.feature_area}
                    </div>
                )}

                {/* Session ID */}
                {event.session_id && (
                    <div style={{
                        fontSize: 9, color: '#374151', flexShrink: 0,
                        fontFamily: 'monospace',
                    }}>
                        sid:{event.session_id.slice(0, 8)}…
                    </div>
                )}

                {/* Deploy version */}
                {event.deployment_version && (
                    <div style={{
                        fontSize: 9, fontFamily: 'monospace',
                        color: '#5b21b6', flexShrink: 0,
                    }}>
                        v:{event.deployment_version.slice(0, 8)}
                    </div>
                )}

                {hasPreview && (
                    expanded ? <ChevronUp size={12} color="#475569" /> : <ChevronDown size={12} color="#475569" />
                )}
            </div>

            {expanded && hasPreview && (
                <div style={{
                    padding: '6px 16px 10px 42px',
                    background: 'rgba(99,102,241,0.04)',
                }}>
                    <pre style={{
                        margin: 0, fontSize: 10, color: '#94a3b8',
                        fontFamily: 'monospace', overflowX: 'auto',
                    }}>
                        {JSON.stringify(preview, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// USER TIMELINE DRAWER
// ─────────────────────────────────────────────────────────────────────────────

const UserTimelineDrawer = ({ userUuid, open, onClose, days = 7 }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!userUuid) return;
        setLoading(true);
        setError(null);
        try {
            const result = await apiFetch(`/user-timeline/${userUuid}?days=${days}`);
            setData(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userUuid, days]);

    useEffect(() => {
        if (open && userUuid) {
            load();
        } else {
            setData(null);
        }
    }, [open, userUuid, load]);

    if (!open) return null;

    const events = data?.events || [];

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                }}
            />

            {/* Drawer */}
            <div style={{
                position: 'fixed', right: 0, top: 0, bottom: 0,
                width: 520, background: '#0d1526',
                border: '1px solid #1e293b',
                display: 'flex', flexDirection: 'column',
                zIndex: 1001,
                boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 20px',
                    borderBottom: '1px solid #1e293b',
                    flexShrink: 0,
                }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
                            User Timeline
                        </div>
                        <div style={{
                            fontSize: 11, fontFamily: 'monospace', color: '#64748b', marginTop: 2,
                        }}>
                            {userUuid}
                        </div>
                    </div>
                    <button
                        id="user-timeline-close-btn"
                        onClick={onClose}
                        style={{
                            background: 'transparent', border: 'none',
                            color: '#64748b', cursor: 'pointer', padding: 4,
                            borderRadius: 6,
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Meta */}
                <div style={{
                    padding: '10px 20px', borderBottom: '1px solid #1e293b',
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: 16,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
                        <Clock size={11} />
                        Last {days} days
                    </div>
                    {data && (
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                            {data.total} event{data.total !== 1 ? 's' : ''} found
                        </div>
                    )}
                    {data?.total === 50 && (
                        <div style={{ fontSize: 10, color: '#f59e0b' }}>Showing last 50</div>
                    )}
                </div>

                {/* Legend */}
                <div style={{
                    padding: '8px 20px', borderBottom: '1px solid #1e293b',
                    display: 'flex', flexWrap: 'wrap', gap: 10, flexShrink: 0,
                }}>
                    {Object.entries(EVENT_CATEGORY_COLORS).slice(0, 6).map(([cat, color]) => (
                        <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
                            <span style={{ fontSize: 9, color: '#475569' }}>{cat}</span>
                        </div>
                    ))}
                </div>

                {/* Event list */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {loading && (
                        <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                            Loading timeline…
                        </div>
                    )}
                    {error && (
                        <div style={{
                            margin: 16, padding: '10px 14px', borderRadius: 8,
                            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                            color: '#fca5a5', fontSize: 12,
                        }}>
                            {error}
                        </div>
                    )}
                    {!loading && !error && events.length === 0 && (
                        <div style={{ padding: 32, textAlign: 'center', color: '#475569', fontSize: 13 }}>
                            No workspace_events found for this user in the last {days} days.
                            <div style={{ fontSize: 11, marginTop: 8, color: '#334155' }}>
                                Events populate as telemetry fires from the frontend.
                            </div>
                        </div>
                    )}
                    {events.map((ev, idx) => (
                        <EventRow key={ev.id || idx} event={ev} index={idx} />
                    ))}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '10px 20px',
                    borderTop: '1px solid #1e293b',
                    flexShrink: 0,
                    fontSize: 10, color: '#334155',
                }}>
                    Sourced from workspace_events · user_uuid match · newest first
                </div>
            </div>
        </>
    );
};

export default UserTimelineDrawer;
